// src/pages/BucketRelease.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { format, addDays, isPast, differenceInDays } from 'date-fns';

export default function BucketRelease() {
  const [reps, setReps] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState({});

  useEffect(() => {
    async function load() {
      const [repSnap, bucketSnap, ordSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'repBuckets')),
        getDocs(collection(db, 'orders')),
      ]);
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const bmap = {};
      bucketSnap.docs.forEach(d => { bmap[d.id] = d.data(); });
      setBuckets(bmap);
      setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  // Find last install date for each rep
  function getLastInstall(repId, repName) {
    const myOrders = orders.filter(o =>
      (o.repId === repId || o.repName === repName) && o.status === 'Completed' && o.date
    );
    if (!myOrders.length) return null;
    const dates = myOrders.map(o => new Date(o.date)).filter(d => !isNaN(d));
    return dates.length ? new Date(Math.max(...dates)) : null;
  }

  // Build rows for terminated reps with bucket balances
  const terminatedRows = reps
    .filter(r => r.terminationDate && (buckets[r.id]?.bucketBalance || 0) > 0)
    .map(rep => {
      const lastInstall = getLastInstall(rep.id, rep.name);
      const releaseDate = lastInstall ? addDays(lastInstall, 90) : null;
      const eligible = releaseDate ? isPast(releaseDate) : false;
      const daysLeft = releaseDate && !eligible ? differenceInDays(releaseDate, new Date()) : 0;
      const bucketBalance = buckets[rep.id]?.bucketBalance || 0;
      return { rep, lastInstall, releaseDate, eligible, daysLeft, bucketBalance };
    })
    .sort((a, b) => (a.eligible ? -1 : 1) - (b.eligible ? -1 : 1) || a.daysLeft - b.daysLeft);

  async function releaseBucket(repId) {
    setReleasing(r => ({ ...r, [repId]: true }));
    await updateDoc(doc(db, 'repBuckets', repId), { bucketBalance: 0, releasedAt: new Date().toISOString() });
    setBuckets(b => ({ ...b, [repId]: { ...b[repId], bucketBalance: 0 } }));
    setReleasing(r => ({ ...r, [repId]: false }));
  }

  const totalHeld = Object.values(buckets).reduce((a, b) => a + (b.bucketBalance || 0), 0);
  const eligibleTotal = terminatedRows.filter(r => r.eligible).reduce((a, r) => a + r.bucketBalance, 0);

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Bucket release</h1>
          <p style={S.sub}>Terminated reps · 90-day hold after last install</p>
        </div>
      </div>

      <div style={S.metrics}>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Total held in buckets</div>
          <div style={{ ...S.metricValue, color: '#f5a623' }}>${Math.round(totalHeld).toLocaleString()}</div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Eligible for release</div>
          <div style={{ ...S.metricValue, color: '#00d4a0' }}>${Math.round(eligibleTotal).toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>{terminatedRows.filter(r => r.eligible).length} reps ready</div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Pending (still in hold)</div>
          <div style={{ ...S.metricValue, color: '#5599cc' }}>{terminatedRows.filter(r => !r.eligible).length} reps</div>
        </div>
      </div>

      <div style={S.tableCard}>
        <table style={S.table}>
          <thead>
            <tr>{['Rep', 'Terminated', 'Last install', 'Release date', 'Days left', 'Bucket balance', 'Status', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {terminatedRows.map(({ rep, lastInstall, releaseDate, eligible, daysLeft, bucketBalance }) => (
              <tr key={rep.id}>
                <td style={S.td}>
                  <div style={S.repName}>{rep.name}</div>
                  <div style={{ fontSize: 10, color: '#555' }}>{rep.role}</div>
                </td>
                <td style={{ ...S.td, color: '#888' }}>{rep.terminationDate}</td>
                <td style={{ ...S.td, color: '#888' }}>{lastInstall ? format(lastInstall, 'MM/dd/yyyy') : '—'}</td>
                <td style={{ ...S.td, color: eligible ? '#00d4a0' : '#888' }}>{releaseDate ? format(releaseDate, 'MM/dd/yyyy') : '—'}</td>
                <td style={{ ...S.td, color: eligible ? '#00d4a0' : '#f5a623' }}>
                  {eligible ? '✓ Eligible' : `${daysLeft} days`}
                </td>
                <td style={{ ...S.td, color: '#f5a623', fontWeight: 600 }}>${Math.round(bucketBalance).toLocaleString()}</td>
                <td style={S.td}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5,
                    background: eligible ? '#1a2e24' : '#2a2010',
                    color: eligible ? '#00d4a0' : '#f5a623' }}>
                    {eligible ? 'Ready to release' : 'In hold period'}
                  </span>
                </td>
                <td style={S.td}>
                  {eligible && (
                    <button
                      style={S.releaseBtn}
                      onClick={() => releaseBucket(rep.id)}
                      disabled={releasing[rep.id]}
                    >
                      {releasing[rep.id] ? 'Releasing...' : 'Release funds'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {terminatedRows.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: '#444', padding: 40 }}>
                No terminated reps with bucket balances. Set a termination date on a rep's profile to track their 90-day hold.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 },
  metricCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '16px 18px' },
  metricLabel: { fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  metricValue: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.5px' },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '10px 12px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
  repName: { fontWeight: 500, color: '#fff' },
  releaseBtn: { background: '#00d4a0', border: 'none', borderRadius: 6, padding: '5px 12px', color: '#000', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
};
